import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ExternalLink } from 'lucide-react';
import { createSectionTracker } from '../../services/funnelTracker';

interface Testimonial {
  name: string;
  role: string;
  before: string;
  after: string;
  quote: string;
  proofUrl?: string;
  proofLabel?: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Camila Cardenal',
    role: 'Pastelera → AI Engineer',
    before: 'Trabajaba en pesos en Argentina, buscando un cambio real',
    after: 'Consiguió trabajo como AI Engineer en 4 meses, cobrando en USD para US',
    quote:
      'De trabajar en pesos en Argentina a trabajar en dólares para US. Tu futuro se construye con las decisiones que hoy te animás a tomar. De pastelera a AI Engineer: con trabajo, dedicación y constancia… todo se puede.',
    proofUrl: 'https://www.linkedin.com/posts/camila-cardenal-451808181_nuevocomienzo-aiengineer-cleartech-activity-7413927672034000896-KAtl',
    proofLabel: 'Ver en LinkedIn',
  },
  {
    name: 'Mauro Cortellezzi',
    role: 'Mentorías → Data Engineer en Tekton Labs',
    before: 'Sin respuestas de reclutadores, buscando su oportunidad en datos',
    after: 'Cambios de LinkedIn el lunes, el miércoles ya le llegaron propuestas. Trabajo en Tekton Labs.',
    quote:
      'Conseguí trabajo en Tekton Labs gracias a tus mentorías. Lo que hablamos en las entrevistas me recontra sirvió, usé todo lo que me dijiste. Las preguntas de SQL las saqué de una por haberlo practicado.',
    proofUrl: 'https://www.linkedin.com/posts/luciano-argolo_felicidades-mauro-cortellezzi-por-tu-nuevo-activity-7392584149288546305-11F_',
    proofLabel: 'Ver en LinkedIn',
  },
  {
    name: 'Ramiro Crespi',
    role: 'Sin experiencia → Jr Data Engineer',
    before: 'Sin experiencia laboral en datos, buscando su primer trabajo',
    after: 'A los 2 meses consiguió su primer trabajo como Data Engineer',
    quote:
      'Me ayudó a definir bien mis prioridades y entender por dónde ir. Pero lo más importante fue que me dio la confianza para estar cómodo en las entrevistas. A los 2 meses conseguí mi primer trabajo. Muy agradecido!',
    proofUrl: 'https://www.linkedin.com/feed/update/urn:li:ugcPost:7384564293033340928',
    proofLabel: 'Ver en LinkedIn',
  },
  {
    name: 'Matías B.',
    role: 'Mentorías → +50% de sueldo',
    before: 'Sabía técnicamente pero no lograba comunicar su valor en entrevistas',
    after: 'Consiguió un 50% más de sueldo en una empresa para US',
    quote:
      'Con las mentorías mejoré mi habilidad de comunicación y entendí mi valor. Conseguí un 50% más en mano.',
    proofUrl: 'https://www.tiktok.com/@lucianoargolo0/video/7608273245288107271',
    proofLabel: 'Ver en TikTok',
  },
  {
    name: 'Julián Mendelewicz',
    role: 'Ciencias sociales → Data Engineer en ~2 meses',
    before: 'Sin base técnica, buscando un cambio de carrera real',
    after: 'Consiguió su primer trabajo como Data Engineer en ~2 meses',
    quote:
      'Vi el caso de una alumna que pasó de pastelera a Ingeniera de IA en 4 meses. Los resultados eran reales y decidí invertir.',
    proofUrl: 'https://www.linkedin.com/posts/julian-mendelewicz_quer%C3%ADa-pasar-a-comentar-que-hace-unas-semanas-activity-7455687524775211010-OHVH/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAByKCd4BfG9TAkC3uxx3f5i_TmDfdHHmHncQue',
    proofLabel: 'Ver en LinkedIn',
  },
];

export default function SocialProof() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tracker = createSectionTracker('social_proof');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Resultados reales de personas como vos
          </h2>
          <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
            No son testimonios inventados. Son leads reales que tomaron acción.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 border border-green-500/20 rounded-2xl p-6 sm:p-8 hover:border-green-500/40 transition-all duration-300 flex flex-col"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>

              {/* Quote */}
              <div className="flex-grow">
                <Quote className="w-6 h-6 text-green-500/40 mb-2" />
                <p className="text-gray-300 italic leading-relaxed text-sm sm:text-base">
                  "{testimonial.quote}"
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-700 my-5" />

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                    {testimonial.proofUrl && (
                      <a
                        href={testimonial.proofUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-medium hover:bg-green-500/30 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {testimonial.proofLabel || 'Ver prueba'}
                      </a>
                    )}
                  </div>
                  <p className="text-green-400 text-xs font-medium">{testimonial.role}</p>
                </div>
              </div>

              {/* Before/After */}
              <div className="mt-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-red-400 text-xs font-bold mt-0.5 flex-shrink-0">ANTES:</span>
                  <span className="text-gray-500 text-xs">{testimonial.before}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 text-xs font-bold mt-0.5 flex-shrink-0">DESPUÉS:</span>
                  <span className="text-gray-400 text-xs">{testimonial.after}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 bg-gradient-to-r from-green-600/10 to-blue-600/10 border border-green-500/20 rounded-2xl p-6 sm:p-8"
        >
          <div className="grid grid-cols-2 gap-6 text-center max-w-md mx-auto">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-green-400">+300</div>
              <div className="text-gray-400 text-sm mt-1">Alumnos capacitados</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-green-400">+40</div>
              <div className="text-gray-400 text-sm mt-1">Profesionales mentoreados</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
